import React from "react";
import { Rnd } from "react-rnd";

export type DetailPanelBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export const DETAIL_PANEL_MIN_WIDTH = 640;
export const DETAIL_PANEL_MIN_HEIGHT = 360;
export const DETAIL_PANEL_DEFAULT_BOUNDS: DetailPanelBounds = {
  x: 16,
  y: 16,
  width: 980,
  height: 560,
};

interface Props {
  title: string;
  bounds: DetailPanelBounds;
  children: React.ReactNode;
  onBoundsChange: (bounds: DetailPanelBounds) => void;
  onClose: () => void;
}

// Dashboard detail shell. It owns only drag/resize chrome so each widget can
// keep its own query and content component separate from the dashboard page.
const DashboardDetailPanel: React.FC<Props> = ({
  title,
  bounds,
  children,
  onBoundsChange,
  onClose,
}) => {
  return (
    <Rnd
      bounds="parent"
      dragHandleClassName="dashboard-detail-drag-handle"
      cancel=".dashboard-detail-no-drag"
      minWidth={DETAIL_PANEL_MIN_WIDTH}
      minHeight={DETAIL_PANEL_MIN_HEIGHT}
      size={{ width: bounds.width, height: bounds.height }}
      position={{ x: bounds.x, y: bounds.y }}
      onDragStop={(event, data) => {
        onBoundsChange({
          ...bounds,
          x: data.x,
          y: data.y,
        });
      }}
      onResizeStop={(event, direction, ref, delta, position) => {
        onBoundsChange({
          x: position.x,
          y: position.y,
          width: ref.offsetWidth,
          height: ref.offsetHeight,
        });
      }}
      className="z-30"
      onMouseDown={(event: MouseEvent) => event.stopPropagation()}
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
      <div className="flex h-full w-full min-w-0 flex-col rounded-lg border border-gray-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.18)]">
        <div className="dashboard-detail-drag-handle flex h-11 shrink-0 cursor-move items-center justify-between border-b border-gray-200 px-4">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <button
            type="button"
            className="dashboard-detail-no-drag h-7 rounded px-2 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="dashboard-detail-no-drag min-h-0 w-full min-w-0 flex-1">
          {children}
        </div>
      </div>
    </Rnd>
  );
};

export default DashboardDetailPanel;
